import { useCallback, useEffect, useMemo, useRef, type RefObject, type UIEvent } from 'react';
import debounce from 'lodash/debounce';
import { getWindow } from './getWindow';

const THROTTLE_TIMEOUT = 100;
const RESTORATION_DEBOUNCE_TIME = 500;
const SCROLL_STORAGE_LIMIT = 100;

const scrollStorage = new Map<string, number>();

const getStoredScrollTop = (key: string) => {
    if (!getWindow()) {
        return 0;
    }

    return scrollStorage.get(key) ?? 0;
};

const setStoredScrollTop = (key: string, scrollTop: number) => {
    if (!getWindow()) {
        return;
    }

    scrollStorage.delete(key);
    scrollStorage.set(key, scrollTop);

    if (scrollStorage.size > SCROLL_STORAGE_LIMIT) {
        const oldestKey = scrollStorage.keys().next().value;

        if (oldestKey !== undefined) {
            scrollStorage.delete(oldestKey);
        }
    }
};

export const clearStoredScrollPositions = (shouldClear: (key: string) => boolean) => {
    if (!getWindow()) {
        return;
    }

    scrollStorage.forEach((_scrollTop, key) => {
        if (shouldClear(key)) {
            scrollStorage.delete(key);
        }
    });
};

export const useResetScroll = (
    containerRef: RefObject<HTMLDivElement>,
    storageKey: string,
    isReady?: boolean,
) => {
    const saveEnabledRef = useRef(false);
    const saveScroll = useMemo(
        () =>
            debounce((scrollTop: number) => {
                setStoredScrollTop(storageKey, scrollTop);
            }, THROTTLE_TIMEOUT),
        [storageKey],
    );

    const onScroll = useCallback(
        (event: UIEvent<HTMLDivElement>) => {
            if (saveEnabledRef.current) {
                saveScroll(event.currentTarget.scrollTop);
            }
        },
        [saveScroll],
    );

    useEffect(
        () => () => {
            saveScroll.flush();
            saveScroll.cancel();
        },
        [saveScroll],
    );

    useEffect(() => {
        const containerElement = containerRef.current;

        if (!containerElement || !isReady) {
            saveEnabledRef.current = false;

            return;
        }

        const storedScrollTop = getStoredScrollTop(storageKey);
        const scrollTop = storedScrollTop > 0 ? storedScrollTop : 0;
        saveEnabledRef.current = false;

        const restorationStartedAt = Date.now();
        let restoreFrameId = 0;
        const restorationTimeoutId = setTimeout(() => {
            cancelAnimationFrame(restoreFrameId);
            containerElement.scrollTo({ top: scrollTop });
            saveEnabledRef.current = true;
        }, RESTORATION_DEBOUNCE_TIME);
        const restoreScroll = () => {
            containerElement.scrollTo({ top: scrollTop });

            if (
                Math.abs(containerElement.scrollTop - scrollTop) > 1 &&
                Date.now() - restorationStartedAt < RESTORATION_DEBOUNCE_TIME
            ) {
                restoreFrameId = requestAnimationFrame(restoreScroll);

                return;
            }

            clearTimeout(restorationTimeoutId);
            saveEnabledRef.current = true;
        };
        containerElement.scrollTo({ top: scrollTop });
        restoreFrameId = requestAnimationFrame(restoreScroll);

        return () => {
            cancelAnimationFrame(restoreFrameId);
            clearTimeout(restorationTimeoutId);
            saveEnabledRef.current = false;
        };
    }, [containerRef, isReady, storageKey]);

    return { onScroll };
};
